import { useState, useCallback } from 'react';
import { addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import type { Task } from '@/services/task.service';
import { PlannerHeader } from './PlannerHeader';
import { PlannerGrid } from './PlannerGrid';
import { usePlannerData } from './usePlannerData';
import type { PlannerPeriod, PlannerTask } from './plannerUtils';

interface TaskPlannerViewProps {
  tasks: Task[];
  workingDays?: string[];
  dailyHours?: number;
  onTaskClick?: (taskId: string) => void;
}

export function TaskPlannerView({
  tasks,
  workingDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  dailyHours = 8,
  onTaskClick,
}: TaskPlannerViewProps) {
  const [period, setPeriod] = useState<PlannerPeriod>('week');
  const [baseDate, setBaseDate] = useState(() => new Date());

  const { periodDays, plannerTasks, dayCapacities, periodLabel } = usePlannerData({
    tasks,
    workingDays,
    dailyHours,
    period,
    baseDate,
  });

  const handlePrevious = useCallback(() => {
    setBaseDate((current) => {
      switch (period) {
        case 'week': return subWeeks(current, 1);
        case '2-weeks': return subWeeks(current, 2);
        case 'month': return subMonths(current, 1);
        default: return subWeeks(current, 1);
      }
    });
  }, [period]);

  const handleNext = useCallback(() => {
    setBaseDate((current) => {
      switch (period) {
        case 'week': return addWeeks(current, 1);
        case '2-weeks': return addWeeks(current, 2);
        case 'month': return addMonths(current, 1);
        default: return addWeeks(current, 1);
      }
    });
  }, [period]);

  const handleToday = useCallback(() => {
    setBaseDate(new Date());
  }, []);

  const handleTaskClick = useCallback(
    (task: PlannerTask) => {
      onTaskClick?.(task.id);
    },
    [onTaskClick],
  );

  return (
    <div className="space-y-0 w-full max-w-full min-w-0">
      <PlannerHeader
        period={period}
        onPeriodChange={setPeriod}
        periodLabel={periodLabel}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onToday={handleToday}
      />
      <PlannerGrid
        periodDays={periodDays}
        dayCapacities={dayCapacities}
        plannerTasks={plannerTasks}
        period={period}
        onTaskClick={handleTaskClick}
      />
    </div>
  );
}
